import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  interpretarErroDeLimite,
  invalidarUsoDoPlano,
  mensagemDeUpgrade,
} from '@/lib/planoLimites';

const CLASSE_CAMPO =
  'w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300';

// Conjunto fechado, e nao texto livre: relatorio por tipo so agrega se o tipo
// for sempre a mesma palavra (ADR-0011).
const TIPOS_DE_IMOVEL = ['Apartamento', 'Cobertura', 'Casa', 'Sala comercial', 'Loja', 'Garagem'];

const IMOVEL_VAZIO = {
  unidade: '',
  torre_id: '',
  andar: '',
  tipo: 'Apartamento',
  area: '',
  proprietario: '',
  locatario: '',
  observacao: '',
};

interface ImovelRecord {
  id: string;
  condominio_id: string;
  torre_id?: string;
  unidade: string;
  andar?: string;
  proprietario?: string;
  locatario?: string;
  status?: string;
  ativo: boolean;
  tipo?: string;
  area?: number;
  planta?: string;
  carro?: number;
  observacao?: string;
  manutencoes?: any[];
  locacoes?: any[];
  torre_nome?: string;
}

interface ImoveisTabProps {
  condoId: string;
  torres?: Array<{ id: string; nome: string }>;
}

export default function ImoveisTab({
  condoId,
}: {
  condoId: string;
  torres?: Array<{ id: string; nome: string }>;
}) {
  const [imoveis, setImoveis] = useState<ImovelRecord[]>([]);
  const [torres, setTorres] = useState<Array<{ id: string; nome: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ImovelRecord | null>(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [criando, setCriando] = useState(false);
  const [novoImovel, setNovoImovel] = useState({ ...IMOVEL_VAZIO });
  const [salvando, setSalvando] = useState(false);
  const [edicao, setEdicao] = useState({ ...IMOVEL_VAZIO });
  const [excluindo, setExcluindo] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // As torres sao buscadas antes dos imoveis, e nao em paralelo: o nome da torre
  // sai dessa lista, e buscando junto o primeiro carregamento sempre caia em
  // "Torre unica" — a lista ainda estava vazia quando o imovel era mapeado.
  const fetchImoveis = useCallback(async () => {
    if (!condoId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: torresData } = await supabase
        .from('torres')
        .select('id, nome')
        .eq('condominio_id', condoId);

      const listaTorres = (torresData || []) as Array<{ id: string; nome: string }>;
      setTorres(listaTorres);

      const { data, error: fErr } = await supabase
        .from('imoveis')
        .select('*')
        .eq('condominio_id', condoId)
        .order('unidade', { ascending: true });

      if (fErr) {
        setError(fErr.message);
      } else {
        const mapped = (data || []).map((item: any) => ({
          ...item,
          torre_nome: listaTorres.find((t) => t.id === item.torre_id)?.nome || 'Torre única',
        }));
        setImoveis(mapped as ImovelRecord[]);
        // Atualizacao funcional: le o selecionado atual sem virar dependencia
        // do callback, que recarregaria a lista a cada clique.
        if (mapped.length > 0) {
          setSelected((atual) => atual ?? (mapped[0] as ImovelRecord));
        }
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar imóveis');
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    void fetchImoveis();
  }, [fetchImoveis]);

  const toggleAtivo = async (id: string) => {
    const imovel = imoveis.find((i) => i.id === id);
    if (!imovel) return;
    const novoAtivo = !imovel.ativo;
    const { error: uErr } = await supabase
      .from('imoveis')
      .update({ ativo: novoAtivo })
      .eq('id', id);
    if (uErr) {
      showToast(`Erro: ${uErr.message}`, 'error');
      return;
    }
    setImoveis((prev) => prev.map((i) => (i.id === id ? { ...i, ativo: novoAtivo } : i)));
    showToast(
      `Unidade "${imovel.unidade}" ${novoAtivo ? 'reativada' : 'desativada'} com sucesso.`,
      novoAtivo ? 'success' : 'info',
    );
  };

  // O painel de gestao acompanha o imovel selecionado. Antes ele usava
  // `defaultValue`, que so le o valor no primeiro render: trocar de unidade
  // mantinha na tela os dados da anterior, e "Salvar" nao existia (P-35).
  useEffect(() => {
    if (!selected) return;
    setEdicao({
      unidade: selected.unidade || '',
      torre_id: selected.torre_id || '',
      andar: selected.andar || '',
      tipo: selected.tipo || 'Apartamento',
      area: selected.area != null ? String(selected.area) : '',
      proprietario: selected.proprietario || '',
      locatario: selected.locatario || '',
      observacao: selected.observacao || '',
    });
  }, [selected]);

  const camposDoImovel = (form: typeof IMOVEL_VAZIO) => ({
    unidade: form.unidade.trim(),
    torre_id: form.torre_id || null,
    andar: form.andar.trim() || null,
    tipo: form.tipo,
    area: form.area ? Number(form.area) : null,
    proprietario: form.proprietario.trim() || null,
    locatario: form.locatario.trim() || null,
    observacao: form.observacao.trim() || null,
  });

  const criarImovel = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!condoId || !novoImovel.unidade.trim()) return;

    setSalvando(true);
    const { data, error: iErr } = await supabase
      .from('imoveis')
      .insert({ condominio_id: condoId, ativo: true, ...camposDoImovel(novoImovel) })
      .select('*')
      .single();
    setSalvando(false);

    if (iErr) {
      // O gatilho de limite de unidades (migration 0003) responde aqui: sem a
      // traducao, a tela mostraria a mensagem crua do banco.
      const limite = interpretarErroDeLimite(iErr);
      showToast(limite ? mensagemDeUpgrade(limite) : `Erro ao cadastrar: ${iErr.message}`, 'error');
      return;
    }

    invalidarUsoDoPlano();
    setNovoImovel({ ...IMOVEL_VAZIO });
    setCriando(false);
    showToast(`Unidade "${data.unidade}" cadastrada.`, 'success');
    await fetchImoveis();
    setSelected(data as ImovelRecord);
  };

  const salvarImovel = async () => {
    if (!selected || !edicao.unidade.trim()) return;

    setSalvando(true);
    const { error: uErr } = await supabase
      .from('imoveis')
      .update(camposDoImovel(edicao))
      .eq('id', selected.id);
    setSalvando(false);

    if (uErr) {
      showToast(`Erro ao salvar: ${uErr.message}`, 'error');
      return;
    }

    showToast(`Unidade "${edicao.unidade.trim()}" salva com sucesso.`, 'success');
    await fetchImoveis();
  };

  // Excluir e desativar (ADR-0009): o imovel guarda historico de locacao e de
  // manutencao, e apagar a linha levaria os dois junto.
  const desativarImovel = async () => {
    if (!selected) return;
    setExcluindo(true);
    const { error: uErr } = await supabase
      .from('imoveis')
      .update({ ativo: false })
      .eq('id', selected.id);
    setExcluindo(false);

    if (uErr) {
      showToast(`Erro: ${uErr.message}`, 'error');
      return;
    }

    showToast(`Unidade "${selected.unidade}" desativada.`, 'info');
    setSelected(null);
    await fetchImoveis();
  };

  const imoveisAtivos = imoveis.filter((i) => i.ativo !== false);
  const imoveisInativos = imoveis.filter((i) => i.ativo === false);
  const listaVisivel = mostrarInativos ? imoveis : imoveisAtivos;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={fetchImoveis}
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          {imoveisAtivos.length} imóvel(is) ativo(s)
          {imoveisInativos.length > 0 ? ` — ${imoveisInativos.length} inativo(s)` : ''}
        </p>
        <button
          type="button"
          onClick={() => setCriando(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>Novo Imóvel
        </button>
      </div>

      {imoveisInativos.length > 0 && (
        <div>
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-5 h-5 rounded-md border-2 border-background-300 flex items-center justify-center shrink-0 transition-all duration-200 peer-checked:bg-primary-500 peer-checked:border-primary-500">
              <i className="ri-check-line text-background-50 text-[10px] leading-none scale-0 peer-checked:scale-100 transition-transform duration-200"></i>
            </div>
            <span className="text-sm text-foreground-600">
              Mostrar imóveis inativos ({imoveisInativos.length})
            </span>
          </label>
        </div>
      )}

      {mostrarInativos && imoveisInativos.length > 0 && (
        <div className="p-4 bg-amber-50/60 border border-amber-200/60 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center text-amber-600">
              <i className="ri-error-warning-line"></i>
            </div>
            <p className="text-sm font-medium text-amber-800">
              Imóveis inativos ({imoveisInativos.length})
            </p>
          </div>
          <p className="text-xs text-amber-700 ml-7">
            Imóveis inativos deixam de contar nos acompanhamentos e relatórios. Seus dados continuam
            preservados.
          </p>
        </div>
      )}

      {imoveis.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-home-4-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Nenhum imóvel cadastrado</p>
          <p className="text-sm text-foreground-500 mt-1">
            Cadastre uma unidade por vez em "Novo Imóvel", ou gere a torre inteira de uma vez na
            configuração da torre.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-background-50 border border-background-200/70 rounded-lg">
            <div className="px-5 py-4 border-b border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900">Imóveis cadastrados</h3>
            </div>
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-2">
                        Unidade
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-2 hidden sm:table-cell">
                        Proprietário
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-2 hidden sm:table-cell">
                        Locatário
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-3 py-2">
                        Ativo
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {listaVisivel.map((imovel) => {
                      const isAtivo = imovel.ativo !== false;
                      return (
                        <tr
                          key={imovel.id}
                          onClick={() => setSelected(imovel)}
                          className={`cursor-pointer transition-colors ${selected?.id === imovel.id ? 'bg-primary-50/50' : `hover:bg-background-100/50 ${!isAtivo ? 'opacity-55 bg-amber-50/20' : ''}`}`}
                        >
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-6 h-6 rounded flex items-center justify-center ${isAtivo ? 'bg-background-100' : 'bg-amber-100'}`}
                              >
                                <i
                                  className={`${isAtivo ? 'ri-home-4-line text-foreground-500' : 'ri-home-4-line text-amber-500'} text-xs`}
                                ></i>
                              </div>
                              <div>
                                <p
                                  className={`text-sm font-medium ${isAtivo ? 'text-foreground-800' : 'text-foreground-500'}`}
                                >
                                  {imovel.unidade}
                                </p>
                                <p className="text-xs text-foreground-500">
                                  {imovel.torre_nome} - Andar {imovel.andar}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 hidden sm:table-cell text-sm text-foreground-700">
                            {imovel.proprietario || '-'}
                          </td>
                          <td className="px-3 py-3 hidden sm:table-cell text-sm text-foreground-700">
                            {imovel.locatario || '-'}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAtivo(imovel.id);
                              }}
                              className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${isAtivo ? 'text-foreground-500 hover:bg-amber-50 hover:text-amber-600' : 'text-amber-600 hover:bg-green-50 hover:text-green-600'}`}
                              title={isAtivo ? 'Desativar imóvel' : 'Reativar imóvel'}
                            >
                              <i
                                className={`${isAtivo ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}
                              ></i>
                            </button>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${isAtivo ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}
                            >
                              {isAtivo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {selected && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground-900">Gestão do imóvel</h3>
                {selected.ativo !== false && (
                  <button
                    type="button"
                    onClick={() => void desativarImovel()}
                    disabled={excluindo}
                    className="px-3 py-1.5 rounded-md border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                  >
                    <i className="ri-eye-off-line mr-1"></i>
                    {excluindo ? 'Desativando...' : 'Desativar'}
                  </button>
                )}
              </div>
              <form
                onSubmit={(evento) => {
                  evento.preventDefault();
                  void salvarImovel();
                }}
                className="p-5"
              >
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                  Cadastro
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label
                      htmlFor="imovel_unidade"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Unidade *
                    </label>
                    <input
                      id="imovel_unidade"
                      type="text"
                      required
                      value={edicao.unidade}
                      onChange={(e) => setEdicao({ ...edicao, unidade: e.target.value })}
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_torre"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Torre
                    </label>
                    <select
                      id="imovel_torre"
                      value={edicao.torre_id}
                      onChange={(e) => setEdicao({ ...edicao, torre_id: e.target.value })}
                      className={CLASSE_CAMPO}
                    >
                      <option value="">Sem torre</option>
                      {torres.map((torre) => (
                        <option key={torre.id} value={torre.id}>
                          {torre.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_andar"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Andar
                    </label>
                    <input
                      id="imovel_andar"
                      type="text"
                      value={edicao.andar}
                      onChange={(e) => setEdicao({ ...edicao, andar: e.target.value })}
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_tipo"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Tipo
                    </label>
                    <select
                      id="imovel_tipo"
                      value={edicao.tipo}
                      onChange={(e) => setEdicao({ ...edicao, tipo: e.target.value })}
                      className={CLASSE_CAMPO}
                    >
                      {TIPOS_DE_IMOVEL.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_area"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Área (m²)
                    </label>
                    <input
                      id="imovel_area"
                      type="number"
                      min={0}
                      value={edicao.area}
                      onChange={(e) => setEdicao({ ...edicao, area: e.target.value })}
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_proprietario"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Proprietário
                    </label>
                    <input
                      id="imovel_proprietario"
                      type="text"
                      value={edicao.proprietario}
                      onChange={(e) => setEdicao({ ...edicao, proprietario: e.target.value })}
                      placeholder="Ex: Ana Ribeiro"
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_locatario"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Locatário
                    </label>
                    <input
                      id="imovel_locatario"
                      type="text"
                      value={edicao.locatario}
                      onChange={(e) => setEdicao({ ...edicao, locatario: e.target.value })}
                      placeholder="Ex: Carlos Menezes"
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="imovel_observacao"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Observação
                    </label>
                    <input
                      id="imovel_observacao"
                      type="text"
                      value={edicao.observacao}
                      onChange={(e) => setEdicao({ ...edicao, observacao: e.target.value })}
                      className={CLASSE_CAMPO}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={salvando || !edicao.unidade.trim()}
                    className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
                  >
                    {salvando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {criando && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo_novo_imovel"
        >
          <div
            className="absolute inset-0 bg-foreground-900/40"
            onClick={() => setCriando(false)}
            aria-hidden="true"
          ></div>
          <div className="relative bg-background-50 border border-background-200/70 rounded-lg w-full max-w-lg shadow-lg">
            <div className="p-6 pb-4 border-b border-background-200/70">
              <h3 id="titulo_novo_imovel" className="text-base font-semibold text-foreground-900">
                Novo Imóvel
              </h3>
              <p className="text-sm text-foreground-500 mt-1">
                Uma unidade por vez. Para cadastrar a torre inteira, use "Gerar unidades" na
                configuração da torre.
              </p>
            </div>

            <form
              onSubmit={criarImovel}
              className="p-6 pt-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="novo_imovel_unidade"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Unidade *
                  </label>
                  <input
                    id="novo_imovel_unidade"
                    type="text"
                    required
                    value={novoImovel.unidade}
                    onChange={(e) => setNovoImovel({ ...novoImovel, unidade: e.target.value })}
                    placeholder="Ex: 101"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_torre"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Torre
                  </label>
                  <select
                    id="novo_imovel_torre"
                    value={novoImovel.torre_id}
                    onChange={(e) => setNovoImovel({ ...novoImovel, torre_id: e.target.value })}
                    className={CLASSE_CAMPO}
                  >
                    <option value="">Sem torre</option>
                    {torres.map((torre) => (
                      <option key={torre.id} value={torre.id}>
                        {torre.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_andar"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Andar
                  </label>
                  <input
                    id="novo_imovel_andar"
                    type="text"
                    value={novoImovel.andar}
                    onChange={(e) => setNovoImovel({ ...novoImovel, andar: e.target.value })}
                    placeholder="Ex: 1"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_tipo"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Tipo
                  </label>
                  <select
                    id="novo_imovel_tipo"
                    value={novoImovel.tipo}
                    onChange={(e) => setNovoImovel({ ...novoImovel, tipo: e.target.value })}
                    className={CLASSE_CAMPO}
                  >
                    {TIPOS_DE_IMOVEL.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_area"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Área (m²)
                  </label>
                  <input
                    id="novo_imovel_area"
                    type="number"
                    min={0}
                    value={novoImovel.area}
                    onChange={(e) => setNovoImovel({ ...novoImovel, area: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_proprietario"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Proprietário
                  </label>
                  <input
                    id="novo_imovel_proprietario"
                    type="text"
                    value={novoImovel.proprietario}
                    onChange={(e) => setNovoImovel({ ...novoImovel, proprietario: e.target.value })}
                    placeholder="Ex: Ana Ribeiro"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_locatario"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Locatário
                  </label>
                  <input
                    id="novo_imovel_locatario"
                    type="text"
                    value={novoImovel.locatario}
                    onChange={(e) => setNovoImovel({ ...novoImovel, locatario: e.target.value })}
                    placeholder="Ex: Carlos Menezes"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="novo_imovel_observacao"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Observação
                  </label>
                  <input
                    id="novo_imovel_observacao"
                    type="text"
                    value={novoImovel.observacao}
                    onChange={(e) => setNovoImovel({ ...novoImovel, observacao: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
                <button
                  type="button"
                  onClick={() => {
                    setNovoImovel({ ...IMOVEL_VAZIO });
                    setCriando(false);
                  }}
                  className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando || !novoImovel.unidade.trim()}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {salvando ? 'Cadastrando...' : 'Cadastrar imóvel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : toast.type === 'error' ? 'ri-close-circle-line text-red-600' : 'ri-information-line text-blue-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
